"use client";
import { useCallback, useEffect, useRef, useState } from "react";
const useCarouselAutoplay = (emblaApi, prefersReducedMotion)=>{
    const [autoplayIsPlaying, setAutoplayIsPlaying] = useState(false);
    const [autoplayIsManuallyPaused, setAutoplayIsManuallyPaused] = useState(prefersReducedMotion);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const isManuallyPausedRef = useRef(prefersReducedMotion);
    const hasUserInteractedRef = useRef(false);
    const resetAutoplayOnInteraction = useCallback((callback)=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return void callback();
        autoplay.reset();
        callback();
    }, [
        emblaApi
    ]);
    const toggleAutoplay = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        hasUserInteractedRef.current = true;
        setHasUserInteracted(true);
        if (prefersReducedMotion) {
            const newPausedState = !autoplayIsManuallyPaused;
            isManuallyPausedRef.current = newPausedState;
            setAutoplayIsManuallyPaused(newPausedState);
            if (newPausedState) autoplay.stop();
            else {
                autoplay.reset();
                autoplay.play();
            }
        } else {
            const wasPlaying = autoplay.isPlaying();
            if (wasPlaying) {
                autoplay.stop();
                isManuallyPausedRef.current = true;
                setAutoplayIsManuallyPaused(true);
            } else {
                isManuallyPausedRef.current = false;
                setAutoplayIsManuallyPaused(false);
                autoplay.reset();
                autoplay.play();
            }
        }
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused
    ]);
    const handleToggleClick = useCallback((event)=>{
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        toggleAutoplay();
    }, [
        toggleAutoplay
    ]);
    const manuallyPauseAutoplay = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (autoplay) {
            if (autoplay.isPlaying()) autoplay.stop();
            isManuallyPausedRef.current = true;
            setAutoplayIsManuallyPaused(true);
        }
    }, [
        emblaApi
    ]);
    useEffect(()=>{
        if (!emblaApi || !prefersReducedMotion) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        if (autoplay.isPlaying() && autoplayIsManuallyPaused) autoplay.stop();
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused
    ]);
    useEffect(()=>{
        if (!emblaApi) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        const updateState = ()=>{
            const isPlaying = autoplay.isPlaying();
            setAutoplayIsPlaying(isPlaying);
        };
        updateState();
        const onPlay = ()=>{
            setAutoplayIsPlaying(true);
        };
        const onStop = ()=>{
            setAutoplayIsPlaying(false);
        };
        emblaApi.on('autoplay:play', onPlay).on('autoplay:stop', onStop).on('reInit', updateState);
        return ()=>{
            emblaApi.off('autoplay:play', onPlay).off('autoplay:stop', onStop);
        };
    }, [
        emblaApi
    ]);
    const effectiveAutoplayIsPlaying = prefersReducedMotion ? !autoplayIsManuallyPaused : autoplayIsPlaying;
    return {
        autoplayIsPlaying: effectiveAutoplayIsPlaying,
        autoplayIsManuallyPaused,
        hasUserInteracted,
        toggleAutoplay: handleToggleClick,
        resetAutoplayOnInteraction,
        manuallyPauseAutoplay,
        isManuallyPausedRef,
        hasUserInteractedRef
    };
};
export { useCarouselAutoplay };
